import type { ClientDto } from '@fos/shared/contracts/crm';
import type { ProjectDetail } from '@fos/shared/contracts/projects';
import { Lock, Plus } from 'lucide-react';
import { useState } from 'react';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState } from '../../../ui/States';
import { DecisionsList } from './DecisionsList';
import { ExportMarkdownButton } from './ExportMarkdownDialog';
import { KnowledgeList } from './KnowledgeList';
import { NewNoteDialog } from './NewNoteDialog';

/**
 * Zakładka „Wiedza” klienta albo projektu: decyzje (data, treść, źródło) i notatki / linki / pliki, „Nowa notatka”
 * z wstępnym klientem / projektem oraz „Eksportuj CLIENT.md / PROJECT.md” (z „Eksportuje dane”).
 */
export function KnowledgeTab({
  clientId,
  projectId,
  canAdd = true,
  canExport = true,
  description,
}: {
  clientId?: string;
  projectId?: string;
  canAdd?: boolean;
  canExport?: boolean;
  description?: string;
}) {
  const access = useAccess();
  const [dialogOpen, setDialogOpen] = useState(false);
  if (!access.can('knowledge')) {
    return (
      <Section>
        <EmptyState icon={Lock} title="Brak dostępu do modułu Wiedza" />
      </Section>
    );
  }
  const allowAdd = canAdd && access.can('knowledge', 'edit');
  const exportTarget = projectId
    ? ({ kind: 'project', id: projectId } as const)
    : clientId
      ? ({ kind: 'client', id: clientId } as const)
      : null;
  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-body-s text-fg-muted">
          {description ?? (projectId ? 'Notatki, decyzje i pliki projektu.' : 'Notatki, decyzje i pliki klienta.')}
        </p>
        {canExport && exportTarget && <ExportMarkdownButton kind={exportTarget.kind} id={exportTarget.id} />}
        {allowAdd && (
          <Button variant="secondary" size="s" icon={Plus} onClick={() => setDialogOpen(true)}>
            Nowa notatka
          </Button>
        )}
      </div>
      <Section aria-label="Decyzje">
        <SectionHeader title="Decyzje" />
        <DecisionsList clientId={clientId} projectId={projectId} showProject={!projectId} />
      </Section>
      <Section aria-label="Notatki, linki i pliki">
        <SectionHeader title="Notatki, linki i pliki" />
        <KnowledgeList clientId={clientId} projectId={projectId} excludeDecisions />
      </Section>
      {allowAdd && (
        <NewNoteDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          defaultClientId={clientId ?? null}
          defaultProjectId={projectId ?? null}
        />
      )}
    </div>
  );
}

/** Zakładka „Wiedza” w profilu klienta (`setClientTab('knowledge', ClientKnowledgeTab)`). */
export function ClientKnowledgeTab({ client }: { client: ClientDto }) {
  const archived = client.status === 'archived' || client.archivedAt !== null;
  return <KnowledgeTab clientId={client.id} canAdd={!client.partial && !archived} canExport={!client.partial} />;
}

/** Zakładka „Wiedza” w projekcie (`setProjectTab('knowledge', ProjectKnowledgeTab)`). */
export function ProjectKnowledgeTab({ project }: { project: ProjectDetail }) {
  return <KnowledgeTab projectId={project.id} canAdd={project.archivedAt === null} />;
}
